import React from 'react';
import { useLocation } from 'react-router-dom';

import { LookoCheckbox, LookoModal } from '@looko-design-system';
import dayjs from 'dayjs';
import { useAtom, useAtomValue } from 'jotai';
import { orderBy } from 'lodash-es';

import { sleep } from '@looko-corp/acloset-react-kit';
import { useAsyncEffect } from '@looko-corp/acloset-react-kit/src/react';

import { claimMap } from '@/router/claimMap';
import { sellerMap } from '@/router/managementMaps';
import { orderMap } from '@/router/orderMap';
import { productMap } from '@/router/productMap';

import { notificationPersistAtom } from './atom/notificationAtom';
import { 튜토리얼_노출상태_아톰 } from './atom/tutorialStatusAtom';
import { useQueryPopups } from './services/use-query-popups';

const dayjsFormat = 'YYYY-MM-DD';

export const 팝업_공지 = React.memo(function 팝업_공지() {
  // global state
  const [local, setLocal] = useAtom(notificationPersistAtom);
  const 튜토리얼_노출상태 = useAtomValue(튜토리얼_노출상태_아톰);
  const location = useLocation();

  // axios
  const { data: 팝업_리스트 } = useQueryPopups({ popupTargets: ['POPUP_TARGET_WEB'] });

  // local
  const [다시보지않기, 다시보지않기_변경하기] = React.useState(false);
  const [visible, setVisible] = React.useState(false);

  const contentList = React.useMemo(() => {
    return orderBy(
      팝업_리스트?.popups?.filter(v =>
        local[v.popupID] ? 주어진_날짜가_오늘로부터_하루가_지났는가(dayjs(local[v.popupID])) : true,
      ),
      v => v.createDate,
      'asc',
    );
  }, [팝업_리스트?.popups, local]);

  // handler
  const onConfirm = React.useCallback((popupURL: string) => {
    window.open(popupURL);

    return true;
  }, []);

  const onCancel = React.useCallback(() => {
    setLocal(prev => ({
      ...prev,
      ...contentList.reduce((acc: Record<string, string>, curr) => {
        acc[curr.popupID] = dayjs().format(dayjsFormat);
        return acc;
      }, {}),
    }));
  }, [contentList, setLocal]);

  // effect
  useAsyncEffect(async () => {
    /**
     * 마운트 시, 팝업 공지 모달을 바로 띄우지 않음.
     * (튜토리얼 노출상태로 인해 팝업이 깜빡이는 것을 방지하기 위해)
     */
    await sleep(1500);
    setVisible(true);
  }, [open]);

  if (!팝업_공지_노출_리스트.includes(location.pathname)) {
    return;
  }

  return (
    <LookoModal.Notification
      open={튜토리얼_노출상태 === 'done' && visible && contentList.length > 0}
      disableEscape
      contents={contentList.map((v, index) => ({
        title: v.title,
        subText: v.description,
        imageUrl: v.imageURL,
        confirmButtonText: v.actionButtonName,
        cancelButtonText: contentList.length > 1 ? '모두 닫기' : '닫기',
        onConfirm: () => onConfirm(v.popupURL),
        onCancel: onCancel,
        showAgainSection: (
          <LookoCheckbox
            checked={다시보지않기}
            onChange={async (_, checked) => {
              if (!checked) {
                return;
              }

              다시보지않기_변경하기(checked);

              // 체크했음을 보여주기 위해 300ms 지연 추가
              await sleep(300);

              setLocal(prev => ({
                ...prev,
                [v.popupID]: dayjs('2999-12-31').format(dayjsFormat),
              }));

              다시보지않기_변경하기(false);
            }}
            label="이 공지 다시 보지 않기"
          />
        ),
      }))}
    />
  );
});

function 주어진_날짜가_오늘로부터_하루가_지났는가(date: dayjs.Dayjs) {
  return dayjs().diff(dayjs(date), 'day') > 0;
}

const 팝업_공지_노출_리스트 = [
  sellerMap.dashboard.path,
  ...Object.values(orderMap).map(v => v.path),
  ...Object.values(claimMap).map(v => v.path),
  ...Object.values(productMap).map(v => v.path),
];
